import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./CounterweightPaywall.module.scss"

/**
 * CounterweightPaywall (`/counterweight`) — an original, self-contained
 * NON-ONBOARDING upsell for Private Photo Vault Premium.
 *
 * Divergence brief: claude-paywalls/_ledger.md · concept: claude-paywalls/counterweight.md
 *
 * Every one of the 30 prior concepts pitched a FEATURE or a feeling. None argued
 * the PRICE. This one is a value-anchoring / cost-per-day contrast made physical:
 *   - Layout:      a warm brass BALANCE BEAM. One pan brims with a life of
 *                  full-colour memories + the real protections around them; the
 *                  other holds a single small engraved PRICE COIN. Not a hero,
 *                  card, calendar, arch, receipt, chat, map, book, network, reel,
 *                  player, editor, deed, net, mosaic, conveyor, bars or scrubber.
 *   - Interaction: memories cascade into the pan and the beam SETTLES with spring
 *                  physics; the plan choice IS the counterweight — Yearly is a
 *                  tiny featherlight coin (~10¢/day), Monthly a bigger, heavier,
 *                  worse-value coin (~47¢/day). Picking a plan re-weighs the beam.
 *   - Register:    confident-reassuring, an "obvious yes" — warm, not salesy.
 *   - Art:         LIGHT warm ivory + brass + PHOTOGRAPHIC memory heap (Unsplash);
 *                  brand blue = the kept/protected accent + featured coin.
 *   - Value frame: VALUE-FOR-MONEY / price anchoring ("everything, for pennies a
 *                  day") + honest yearly-vs-monthly contrast — untouched by all 30
 *                  priors (Receipt was backup tally, Featherweight was storage).
 *
 * Honest & App Store compliant: real Premium features only (automatic full-res
 * backup, private/encrypted, every device). Cost-per-day is exact arithmetic on
 * the real prices; the savings % is computed from the real yearly vs monthly. No
 * countdown, no fake scarcity, no data-loss claim. The memory pan is never given
 * a fake dollar "value" — only the honest price is quantified. Yearly is the
 * featured, default plan; monthly is smaller and secondary. Photos are labelled
 * as Unsplash example memories.
 */

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v))

type PlanKey = "yearly" | "monthly"

// The memory heap that piles into the value pan. Slots map to bundled Unsplash
// files in public/counterweight/. Geometry is deterministic so it never jitters.
const MEMORIES = [
  { slot: "p2", x: -34, y: -6, r: -11, s: 1.0 },
  { slot: "p4", x: 30, y: -10, r: 9, s: 1.02 },
  { slot: "p1", x: -8, y: -20, r: -3, s: 1.06 },
  { slot: "p6", x: -46, y: 16, r: -8, s: 0.94 },
  { slot: "p9", x: 44, y: 14, r: 12, s: 0.96 },
  { slot: "p3", x: 12, y: 6, r: 5, s: 1.0 },
  { slot: "p7", x: -22, y: 24, r: -6, s: 0.9 },
  { slot: "p10", x: 26, y: 30, r: 8, s: 0.88 },
  { slot: "p5", x: 0, y: 34, r: -2, s: 0.86 },
] as const

// The real protections that ride on top of the heap — each is a genuine Premium
// capability, rendered as a small sigil chip resting in the pan.
const SIGILS = [
  { key: "backup", label: "Auto-backed up", icon: "cloud" },
  { key: "quality", label: "Full quality", icon: "gem" },
  { key: "private", label: "Private & encrypted", icon: "lock" },
  { key: "devices", label: "Every device", icon: "devices" },
] as const

type IconName = (typeof SIGILS)[number]["icon"]

const Icon = ({ name, size = 15 }: { name: IconName; size?: number }) => {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  }
  switch (name) {
    case "cloud":
      return (
        <svg {...common}>
          <path d="M7 18a4 4 0 0 1-.4-7.98A5.5 5.5 0 0 1 17.5 9.5 3.75 3.75 0 0 1 17 18H7z" />
          <path d="M12 15.5V10m0 0-2 2m2-2 2 2" />
        </svg>
      )
    case "gem":
      return (
        <svg {...common}>
          <path d="M6 3h12l3 5-9 13L3 8l3-5z" />
          <path d="M3 8h18M9 3 6.5 8 12 21l5.5-13L15 3" />
        </svg>
      )
    case "lock":
      return (
        <svg {...common}>
          <rect x="5" y="10.5" width="14" height="10" rx="2.4" />
          <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" />
          <circle cx="12" cy="15" r="1.3" fill="currentColor" stroke="none" />
        </svg>
      )
    case "devices":
      return (
        <svg {...common}>
          <rect x="2.5" y="5" width="13" height="9" rx="1.6" />
          <rect x="16" y="8.5" width="5.5" height="11" rx="1.4" />
          <path d="M6 17.5h6" />
        </svg>
      )
  }
}

const Shield = ({ size = 16 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
    <path
      d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
    <path
      d="M9.2 12.1l2 2 3.6-3.9"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const perDay = (yearlyPrice: number, plan: PlanKey, monthlyPrice: number) =>
  plan === "yearly" ? yearlyPrice / 365 : monthlyPrice / 30.44

const CounterweightPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyNum = yearlyProduct?.price ?? 35.99
  const monthlyNum = monthlyProduct?.price ?? 13.99

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyNum, locale)
    : "$35.99"
  const yearlyPerMonth = formatCurrency(yearlyNum / 12, locale)
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyNum, locale)
    : "$13.99"
  const hasTrial = yearlyProduct?.hasTrial ?? true

  const savingsPct = useMemo(() => {
    if (!monthlyNum) return 0
    return Math.round((1 - yearlyNum / (monthlyNum * 12)) * 100)
  }, [yearlyNum, monthlyNum])

  const [plan, setPlan] = useState<PlanKey>("yearly")

  // per-day readout, formatted in cents when < a currency unit
  const perDayLabel = useMemo(() => {
    const v = perDay(yearlyNum, plan, monthlyNum)
    const cur = locale?.currency ?? "USD"
    if ((cur === "USD" || cur === "EUR" || cur === "GBP") && v < 1) {
      const c = Math.max(1, Math.round(v * 100))
      const sym = cur === "EUR" ? "€" : cur === "GBP" ? "£" : "¢"
      return cur === "USD" ? `${c}¢` : `${sym}${v.toFixed(2)}`
    }
    return formatCurrency(v, locale)
  }, [plan, yearlyNum, monthlyNum, locale])

  // ── The balance ────────────────────────────────────────────────────────────
  // `loaded` counts how many memory tiles have landed in the pan; the beam angle
  // is derived from the load (heavier value pan) minus the counterweight coin.
  const [loaded, setLoaded] = useState(0)
  const [settled, setSettled] = useState(false)
  const timers = useRef<number[]>([])

  useEffect(() => {
    if (reduceMotion) {
      setLoaded(MEMORIES.length)
      setSettled(true)
      return
    }
    // Cascade the memories in, one every ~150ms, then mark settled.
    MEMORIES.forEach((_, i) => {
      const id = window.setTimeout(() => setLoaded(i + 1), 380 + i * 150)
      timers.current.push(id)
    })
    const done = window.setTimeout(
      () => setSettled(true),
      380 + MEMORIES.length * 150 + 200,
    )
    timers.current.push(done)
    return () => {
      timers.current.forEach((t) => window.clearTimeout(t))
      timers.current = []
    }
  }, [reduceMotion])

  // Beam tilt (degrees). Value pan (left) always outweighs the coin — the whole
  // point — so the beam tips left/down. Monthly's heavier coin lets the price
  // side recover a touch (a smaller tip), which honestly reads as "worse value".
  const loadFrac = clamp(loaded / MEMORIES.length, 0, 1)
  const coinWeight = plan === "yearly" ? 0.1 : 0.42 // fraction of a full pan
  const angle = settled
    ? -(9.5 - coinWeight * 9) // ≈ -8.6° yearly, ≈ -5.7° monthly
    : -(loadFrac * 8.5)

  const handleClose = () => native.postNativeEvent("close")
  const handleSubscribe = () => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  const selectPlan = useCallback((p: PlanKey) => setPlan(p), [])

  return (
    <div className={styles.root}>
      <div className={styles.wash} aria-hidden="true" />
      <div className={styles.grain} aria-hidden="true" />

      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark}>
            <Shield size={14} />
          </span>
          <span className={styles.brandText}>
            Private Photo Vault<em>Premium</em>
          </span>
        </div>
        <button
          className={styles.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
            <path
              d="M5 5l14 14M19 5L5 19"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      <div className={styles.intro}>
        <h1 className={styles.title}>
          A whole life on one side.
          <br />
          <span className={styles.titleAccent}>
            Pocket change on the other.
          </span>
        </h1>
        <p className={styles.sub}>
          Premium keeps every photo and video — backed up, full quality,
          private, on every device. Watch what that&rsquo;s worth against what
          it costs.
        </p>
      </div>

      {/* ── THE BALANCE ──────────────────────────────────────────────────── */}
      <div
        className={styles.scale}
        role="img"
        aria-label={`A balance: a life of memories protected by Premium far outweighs ${perDayLabel} a day`}
      >
        {/* pivot + beam */}
        <div className={styles.stand} aria-hidden="true">
          <span className={styles.post} />
          <span className={styles.pivot} />
        </div>

        <div
          className={styles.beam}
          style={{ transform: `rotate(${angle.toFixed(2)}deg)` }}
        >
          <span className={styles.beamBar} aria-hidden="true" />
          <span className={styles.beamCap} data-end="left" aria-hidden="true" />
          <span
            className={styles.beamCap}
            data-end="right"
            aria-hidden="true"
          />

          {/* ── LEFT: the value pan (memories + protections) ── */}
          <div className={`${styles.arm} ${styles.armLeft}`}>
            <span className={styles.chain} aria-hidden="true" />
            <div
              className={styles.pan}
              style={{ transform: `rotate(${(-angle).toFixed(2)}deg)` }}
            >
              <div className={styles.heap}>
                {MEMORIES.map((m, i) => {
                  const shown = i < loaded
                  return (
                    <figure
                      key={m.slot}
                      className={`${styles.tile} ${shown ? styles.tileIn : ""}`}
                      style={{ zIndex: 10 + i }}
                    >
                      <span
                        className={styles.tileInner}
                        style={{
                          transform: `translate(${m.x}px, ${m.y}px) rotate(${m.r}deg) scale(${m.s})`,
                        }}
                      >
                        <img
                          src={`/counterweight/${m.slot}.jpg`}
                          alt=""
                          draggable={false}
                          loading="eager"
                          onError={(e) => {
                            ;(
                              e.currentTarget as HTMLImageElement
                            ).style.opacity = "0"
                          }}
                        />
                      </span>
                    </figure>
                  )
                })}
              </div>
            </div>
          </div>

          {/* ── RIGHT: the counterweight (the price coin) ── */}
          <div className={`${styles.arm} ${styles.armRight}`}>
            <span className={styles.chain} aria-hidden="true" />
            <div
              className={styles.pan}
              style={{ transform: `rotate(${(-angle).toFixed(2)}deg)` }}
            >
              <div
                className={`${styles.coin} ${
                  plan === "yearly" ? styles.coinYearly : styles.coinMonthly
                }`}
                key={plan}
              >
                <span className={styles.coinShine} aria-hidden="true" />
                <span className={styles.coinPer}>{perDayLabel}</span>
                <span className={styles.coinPerUnit}>a day</span>
              </div>
            </div>
          </div>
        </div>
        <span className={styles.base} aria-hidden="true" />
      </div>

      {/* what actually sits in the value pan — the real Premium protections */}
      <div className={`${styles.legend} ${settled ? styles.legendIn : ""}`}>
        {SIGILS.map((s, i) => (
          <span
            key={s.key}
            className={styles.sigil}
            style={{ transitionDelay: `${i * 60}ms` }}
          >
            <span className={styles.sigilIcon}>
              <Icon name={s.icon} size={13} />
            </span>
            {s.label}
          </span>
        ))}
      </div>

      {/* ── THE CHOICE (two counterweights) ─────────────────────────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.plan} ${styles.planYearly} ${
            plan === "yearly" ? styles.planOn : ""
          }`}
          onClick={() => selectPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.planGlow} aria-hidden="true" />
          <span className={styles.planTop}>
            <span className={styles.planName}>Yearly</span>
            {savingsPct > 0 && (
              <span className={styles.saveTag}>
                Best value · save {savingsPct}%
              </span>
            )}
          </span>
          <span className={styles.planPriceRow}>
            <span className={styles.planPrice}>{yearlyPrice}</span>
            <span className={styles.planUnit}>/ year</span>
          </span>
          <span className={styles.planFoot}>
            just {yearlyPerMonth}/mo · lightest on your wallet
          </span>
          <span className={styles.radio} aria-hidden="true" />
        </button>

        <button
          className={`${styles.plan} ${styles.planMonthly} ${
            plan === "monthly" ? styles.planOnMonthly : ""
          }`}
          onClick={() => selectPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.planNameSm}>Monthly</span>
          <span className={styles.planPriceSm}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <div className={styles.dock}>
        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaMain}>
            {plan === "yearly"
              ? hasTrial
                ? "Try Premium free for 7 days"
                : "Get Premium — yearly"
              : "Get Premium — monthly"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? hasTrial
                ? `then ${yearlyPrice}/yr (${perDayLabel}/day) · cancel anytime`
                : `${yearlyPrice}/yr (${perDayLabel}/day) · cancel anytime`
              : `${monthlyPrice}/mo · cancel anytime`}
          </span>
        </button>

        <div className={styles.footerLinks}>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
        </div>
        <p className={styles.credit}>Example memories from Unsplash</p>
      </div>
    </div>
  )
}

export default CounterweightPaywall
